/**
 * Calendar — parent/staff workspace events with weekly plan grid + RSVP +
 * permission slip workflow.
 *
 * Iteration 24 — Tabulex-parity surface.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import { useChild } from "../lib/childContext";
import { api } from "../lib/api";
import Brand from "../components/Brand";
import {
  Calendar as CalendarIcon,
  MapPin,
  Check,
  X,
  HelpCircle,
  ArrowLeft,
  Paperclip,
  FileSignature,
  Sparkles,
} from "lucide-react";

const KIND_LABEL = {
  field_trip: "Field trip",
  parent_meeting: "Parent meeting",
  holiday: "Holiday",
  closure: "Closure",
  celebration: "Celebration",
  info: "Note",
  activity: "Activity",
  staff_meeting: "Staff meeting",
  training: "Training",
};

const KIND_COLOR = {
  field_trip: "#8A9984",
  parent_meeting: "#A89CC8",
  holiday: "#E6BFA6",
  closure: "#D4A85F",
  celebration: "#FFB800",
  info: "#9BA995",
  activity: "#1A1B2E",
  staff_meeting: "#1F3A2A",
  training: "#7E8C72",
};

function fmtWhen(starts) {
  if (!starts) return "—";
  const isDate = /^\d{4}-\d{2}-\d{2}$/.test(starts);
  const d = new Date(starts);
  if (isDate) {
    return d.toLocaleDateString(undefined, {
      weekday: "short",
      month: "short",
      day: "numeric",
    });
  }
  return d.toLocaleString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function fmtDayLabel(d) {
  return d.toLocaleDateString(undefined, { weekday: "short", day: "numeric" });
}

function startOfWeek(d) {
  const x = new Date(d);
  const day = (x.getDay() + 6) % 7; // Monday-first
  x.setDate(x.getDate() - day);
  x.setHours(0, 0, 0, 0);
  return x;
}

function isoDate(d) {
  return d.toISOString().slice(0, 10);
}

function sameDay(iso, dayDate) {
  if (!iso) return false;
  const d = new Date(iso);
  return (
    d.getFullYear() === dayDate.getFullYear() &&
    d.getMonth() === dayDate.getMonth() &&
    d.getDate() === dayDate.getDate()
  );
}

export default function Calendar() {
  const { user } = useAuth();
  const { selected } = useChild();
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [weekEvents, setWeekEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [weekStart, setWeekStart] = useState(startOfWeek(new Date()));
  const [activeSlip, setActiveSlip] = useState(null);
  const [slipText, setSlipText] = useState("");

  // Resolve workspace id from the selected child (so we can hit weekly endpoint).
  const wsId = selected?.workspace_id || null;

  const loadAll = async () => {
    setLoading(true);
    try {
      const r = await api.get("/events/upcoming", { params: { limit: 50 } });
      setEvents(r.data || []);
    } catch {
      // quiet
    } finally {
      setLoading(false);
    }
  };

  const loadWeek = async () => {
    if (!wsId) {
      setWeekEvents([]);
      return;
    }
    try {
      const r = await api.get(`/workspaces/${wsId}/events/week`, {
        params: { start: isoDate(weekStart) },
      });
      setWeekEvents(r.data || []);
    } catch {
      setWeekEvents([]);
    }
  };

  useEffect(() => {
    loadAll();
  }, []);

  useEffect(() => {
    loadWeek();
  }, [wsId, weekStart]);

  const rsvp = async (eventId, status) => {
    try {
      const r = await api.post(`/events/${eventId}/rsvp`, {
        status,
        child_id: selected?.id,
      });
      setEvents((prev) =>
        prev.map((e) => (e.id === eventId ? r.data : e))
      );
      setWeekEvents((prev) =>
        prev.map((e) => (e.id === eventId ? r.data : e))
      );
      toast(
        status === "going"
          ? "Joined, gently."
          : status === "maybe"
          ? "Saved as maybe."
          : "Noted."
      );
    } catch (err) {
      toast(err.response?.data?.detail || "Couldn't update.");
    }
  };

  const submitSlip = async () => {
    if (!activeSlip || !slipText.trim()) return;
    try {
      const r = await api.post(
        `/events/${activeSlip.id}/permission-slip`,
        {
          child_id: selected?.id,
          consent_text: slipText.trim(),
        }
      );
      setEvents((prev) =>
        prev.map((e) => (e.id === activeSlip.id ? r.data : e))
      );
      setWeekEvents((prev) =>
        prev.map((e) => (e.id === activeSlip.id ? r.data : e))
      );
      toast("Permission gently noted.");
      setActiveSlip(null);
      setSlipText("");
    } catch (err) {
      toast(err.response?.data?.detail || "Couldn't sign.");
    }
  };

  const weekDays = useMemo(() => {
    const days = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(weekStart);
      d.setDate(weekStart.getDate() + i);
      days.push(d);
    }
    return days;
  }, [weekStart]);

  return (
    <div data-testid="calendar-screen" className="min-h-screen">
      <header className="px-5 sm:px-10 pt-6 flex items-center justify-between">
        <button
          type="button"
          onClick={() => navigate(-1)}
          data-testid="calendar-back"
          aria-label="Back"
          className="flex items-center gap-2 text-[#6A6863] hover:text-[#2C2B29] transition-colors"
        >
          <ArrowLeft size={18} strokeWidth={1.5} />
          <span className="text-sm">Back</span>
        </button>
        <Brand size="sm" dataTestId="brand-calendar" />
      </header>

      <main className="px-6 sm:px-10 pt-8 pb-32 max-w-3xl mx-auto fade-up">
        <span className="eyebrow">Together</span>
        <h1 className="mt-3 font-serif text-4xl sm:text-5xl tracking-tight leading-none">
          What&rsquo;s ahead.
        </h1>
        <p className="mt-4 text-base text-[#6A6863] font-light max-w-md">
          A quiet calendar — field trips, closures, parent gatherings. RSVP and
          sign permission slips where it helps the team plan.
        </p>

        {/* Weekly plan */}
        {wsId && (
          <section
            data-testid="calendar-week"
            className="mt-10 rounded-3xl bg-white border hairline p-5 sm:p-6"
          >
            <div className="flex items-center justify-between gap-3">
              <span className="eyebrow">Weekly plan</span>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  data-testid="calendar-week-prev"
                  onClick={() => {
                    const d = new Date(weekStart);
                    d.setDate(weekStart.getDate() - 7);
                    setWeekStart(d);
                  }}
                  className="h-8 px-3 rounded-full bg-[#F2EFEA] text-xs tracking-[0.16em] uppercase text-[#525252] hover:bg-[#EAE5EF]"
                >
                  ‹
                </button>
                <button
                  type="button"
                  data-testid="calendar-week-today"
                  onClick={() => setWeekStart(startOfWeek(new Date()))}
                  className="h-8 px-3 rounded-full bg-[#F2EFEA] text-xs tracking-[0.16em] uppercase text-[#525252] hover:bg-[#EAE5EF]"
                >
                  This week
                </button>
                <button
                  type="button"
                  data-testid="calendar-week-next"
                  onClick={() => {
                    const d = new Date(weekStart);
                    d.setDate(weekStart.getDate() + 7);
                    setWeekStart(d);
                  }}
                  className="h-8 px-3 rounded-full bg-[#F2EFEA] text-xs tracking-[0.16em] uppercase text-[#525252] hover:bg-[#EAE5EF]"
                >
                  ›
                </button>
              </div>
            </div>
            <div className="mt-5 grid grid-cols-7 gap-2">
              {weekDays.map((d) => {
                const dayEvts = weekEvents.filter((e) =>
                  sameDay(e.starts_at, d)
                );
                return (
                  <div
                    key={d.toISOString()}
                    data-testid={`calendar-week-day-${d.getDate()}`}
                    className="rounded-2xl bg-[#F2EFEA] p-3 min-h-[100px]"
                  >
                    <div className="text-[10px] tracking-[0.18em] uppercase text-[#525252]">
                      {fmtDayLabel(d)}
                    </div>
                    <ul className="mt-2 space-y-1.5">
                      {dayEvts.map((e) => (
                        <li
                          key={e.id}
                          title={e.title}
                          className="text-[11px] leading-tight rounded-lg px-2 py-1"
                          style={{
                            background: `${KIND_COLOR[e.kind] || "#9BA995"}30`,
                            color: KIND_COLOR[e.kind] || "#9BA995",
                          }}
                        >
                          {e.title}
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        <ol data-testid="calendar-list" className="mt-10 space-y-4">
          {loading && (
            <li className="font-serif italic text-[#6A6863]">A quiet moment…</li>
          )}
          {!loading && events.length === 0 && (
            <li
              data-testid="calendar-empty"
              className="font-serif italic text-[#6A6863] rounded-3xl border hairline bg-white p-7"
            >
              Nothing scheduled. The horizon is calm.
            </li>
          )}
          {events.map((e) => (
            <li
              key={e.id}
              data-testid={`calendar-event-${e.id}`}
              className="rounded-3xl border hairline bg-white p-6 sm:p-7"
            >
              <div className="flex items-start gap-4">
                <span
                  className="h-9 w-9 rounded-full flex items-center justify-center shrink-0"
                  style={{ background: KIND_COLOR[e.kind] || "#9BA995" }}
                  aria-hidden
                >
                  <CalendarIcon
                    size={16}
                    strokeWidth={1.5}
                    className="text-white"
                  />
                </span>
                <div className="flex-1 min-w-0">
                  <div className="eyebrow">
                    {KIND_LABEL[e.kind] || "Note"}
                    {e.group_id ? ` · ${e.group_id}` : ""}
                    {e.workspace_name ? ` · ${e.workspace_name}` : ""}
                  </div>
                  <h3 className="mt-2 font-serif text-xl text-[#2C2B29] leading-snug">
                    {e.title}
                  </h3>
                  <p className="mt-2 text-sm text-[#6A6863]">
                    {fmtWhen(e.starts_at)}
                    {e.location ? (
                      <span className="ml-2 inline-flex items-center gap-1">
                        <MapPin size={11} strokeWidth={1.5} />
                        {e.location}
                      </span>
                    ) : null}
                  </p>
                  {e.body && (
                    <p className="mt-3 text-base text-[#2C2B29]/80 font-light leading-snug whitespace-pre-line">
                      {e.body}
                    </p>
                  )}

                  {e.attachments?.length > 0 && (
                    <ul
                      data-testid={`calendar-attachments-${e.id}`}
                      className="mt-3 space-y-1"
                    >
                      {e.attachments.map((url) => (
                        <li key={url}>
                          <a
                            href={url}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-2 text-xs text-[#1F3A2A] underline underline-offset-2 hover:text-[#1A1B2E]"
                          >
                            <Paperclip size={11} strokeWidth={1.6} />
                            {url.length > 50 ? `${url.slice(0, 50)}…` : url}
                          </a>
                        </li>
                      ))}
                    </ul>
                  )}

                  {e.reminders?.length > 0 && (
                    <p className="mt-3 text-[10px] tracking-[0.18em] uppercase text-[#525252] inline-flex items-center gap-1.5">
                      <Sparkles size={10} strokeWidth={1.6} />
                      Gentle reminder · {e.reminders.join(" · ")}
                    </p>
                  )}

                  {e.permission_slip_required && user?.role === "parent" && (
                    <div className="mt-5">
                      {e.permission_slip_signed ? (
                        <span
                          data-testid={`calendar-slip-signed-${e.id}`}
                          className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#132619]/10 text-[#1A1B2E] text-xs tracking-[0.16em] uppercase"
                        >
                          <Check size={12} strokeWidth={1.6} />
                          Permission gently noted
                        </span>
                      ) : (
                        <button
                          type="button"
                          onClick={() => {
                            setActiveSlip(e);
                            setSlipText(
                              `I, ${user?.name || ""}, agree for ${
                                selected?.name || "my child"
                              } to attend.`
                            );
                          }}
                          data-testid={`calendar-slip-open-${e.id}`}
                          className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#D4A85F]/20 text-[#9C7826] text-xs tracking-[0.16em] uppercase font-medium hover:bg-[#D4A85F]/30"
                        >
                          <FileSignature size={12} strokeWidth={1.6} />
                          Sign permission slip
                        </button>
                      )}
                    </div>
                  )}

                  {e.rsvp_required && user?.role === "parent" && (
                    <div className="mt-5 flex flex-wrap gap-2">
                      <RsvpButton
                        active={e.my_rsvp === "going"}
                        onClick={() => rsvp(e.id, "going")}
                        testId={`calendar-rsvp-going-${e.id}`}
                        icon={<Check size={12} strokeWidth={1.5} />}
                        label="Going"
                      />
                      <RsvpButton
                        active={e.my_rsvp === "maybe"}
                        onClick={() => rsvp(e.id, "maybe")}
                        testId={`calendar-rsvp-maybe-${e.id}`}
                        icon={<HelpCircle size={12} strokeWidth={1.5} />}
                        label="Maybe"
                      />
                      <RsvpButton
                        active={e.my_rsvp === "not_going"}
                        onClick={() => rsvp(e.id, "not_going")}
                        testId={`calendar-rsvp-not-going-${e.id}`}
                        icon={<X size={12} strokeWidth={1.5} />}
                        label="Can't make it"
                      />
                      {(e.rsvp_going > 0 || e.rsvp_maybe > 0) && (
                        <span className="ml-auto text-xs text-[#6A6863] self-center">
                          {e.rsvp_going} going · {e.rsvp_maybe} maybe
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </li>
          ))}
        </ol>
      </main>

      {/* Permission slip modal */}
      {activeSlip && (
        <div
          data-testid="calendar-slip-modal"
          className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
          onClick={() => setActiveSlip(null)}
        >
          <div
            className="bg-[#FBF9F6] rounded-3xl max-w-md w-full p-7 shadow-2xl"
            onClick={(ev) => ev.stopPropagation()}
          >
            <div className="eyebrow">Permission slip</div>
            <h2 className="mt-2 font-serif text-2xl text-[#171717]">
              {activeSlip.title}
            </h2>
            <p className="mt-1 text-sm text-[#6A6863]">
              For {selected?.name || "your child"} · {fmtWhen(activeSlip.starts_at)}
            </p>
            {activeSlip.body && (
              <p className="mt-4 text-sm text-[#2C2B29]/80 font-light">
                {activeSlip.body}
              </p>
            )}
            <textarea
              value={slipText}
              onChange={(ev) => setSlipText(ev.target.value)}
              data-testid="calendar-slip-text"
              rows={3}
              className="orbit-input mt-5 w-full"
            />
            <p className="mt-2 text-xs text-[#6A6863] italic">
              By tapping <em>Give permission</em> you gently confirm this consent.
            </p>
            <div className="mt-5 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setActiveSlip(null)}
                data-testid="calendar-slip-cancel"
                className="btn-ghost !py-2 !px-5 !text-xs"
              >
                Not now
              </button>
              <button
                type="button"
                onClick={submitSlip}
                data-testid="calendar-slip-submit"
                className="btn-primary !py-2 !px-5 !text-xs"
              >
                Give permission
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function RsvpButton({ active, onClick, testId, icon, label }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={
        "inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs transition-colors " +
        (active
          ? "bg-[#132619] text-white"
          : "bg-[#F2EFEA] text-[#2C2B29] hover:bg-[#E6BFA6]/40")
      }
    >
      {icon}
      {label}
    </button>
  );
}
